import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useUserStats } from "../context/UserStatsContext";
import { useSubscription } from "../context/SubscriptionContext";
import InterviewOnboarding from "../components/interview/InterviewOnboarding";
import InterviewSetup from "../components/interview/InterviewSetup";
import InterviewQuestions from "../components/interview/InterviewQuestions";
import InterviewResults from "../components/interview/InterviewResults";
import UserProfileIcon from "../components/UserProfileIcon";
import Logo from "../components/Logo";
import TetrisGame from "../components/TetrisGame";
import {
  InterviewSession,
  InterviewQuestion,
  QuestionCount,
} from "../types";
import { TETRIS_CREDIT_REWARD } from "../types/subscription";

const initialSession: InterviewSession = {
  jobTitle: "",
  roleDescription: undefined,
  questionCount: 5,
  questions: [],
  currentQuestionIndex: 0,
  status: "onboarding",
  hellMode: false,
};

const InterviewPage: React.FC = () => {
  const [session, setSession] = useState<InterviewSession>(initialSession);
  const [showTetris, setShowTetris] = useState(false);
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const { startSession, endSession } = useUserStats();
  const {
    subscription,
    canUseInterview,
    remainingInterviews,
    trackInterviewUse,
    canEarnBonusCredits,
    addBonusCredits,
    bonusCredits,
    isBetaUser,
  } = useSubscription();

  const hasInterviewAccess = canUseInterview();
  const isFreeUser = subscription?.plan === 'free' && !isBetaUser;
  // Track session when interview starts
  useEffect(() => {
    if (session.status === 'interviewing') {
      startSession('interview');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.status]);

  // End session when interview completes
  useEffect(() => {
    const endInterviewSession = async () => {
      if (session.status === 'completed' && session.questions.length > 0) {
        // Calculate average score from all questions
        const answeredQuestions = session.questions.filter(q => q.answer?.evaluation);
        if (answeredQuestions.length > 0) {
          const avgScore = Math.round(
            answeredQuestions.reduce((sum, q) => sum + (q.answer?.evaluation?.score || 0), 0) /
            answeredQuestions.length
          );
          await endSession(avgScore);
        } else {
          await endSession();
        }
      }
    };
    endInterviewSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.status]);

  const handleLogout = async (): Promise<void> => {
    try {
      await logout();
    } catch (err) {
      console.error("Logout error:", err);
    }
  };

  // Tetris handlers
  const handleTetrisComplete = async (linesCleared: number) => {
    if (linesCleared >= 5) {
      await addBonusCredits(TETRIS_CREDIT_REWARD);
    }
    setShowTetris(false);
  };

  const handleStartInterview = async () => {
    // Track interview usage when starting
    await trackInterviewUse();
    setSession((prev) => ({ ...prev, status: "setup" }));
  };

  const handleSetupComplete = (
    jobTitle: string,
    roleDescription: string | undefined,
    questionCount: QuestionCount,
    hellMode: boolean
  ) => {
    setSession((prev) => ({
      ...prev,
      jobTitle,
      roleDescription,
      questionCount,
      hellMode,
      status: "interviewing",
    }));
  };

  const handleQuestionsGenerated = (questions: string[]) => {
    const interviewQuestions: InterviewQuestion[] = questions.map((q, index) => ({
      id: index + 1,
      question: q,
    }));
    setSession((prev) => ({
      ...prev,
      questions: interviewQuestions,
    }));
  };

  const handleAnswerSubmitted = (questionIndex: number, answer: InterviewQuestion["answer"]) => {
    setSession((prev) => {
      const updatedQuestions = [...prev.questions];
      updatedQuestions[questionIndex] = {
        ...updatedQuestions[questionIndex],
        answer,
      };

      const nextIndex = questionIndex + 1;
      const isCompleted = nextIndex >= prev.questions.length;

      return {
        ...prev,
        questions: updatedQuestions,
        currentQuestionIndex: isCompleted ? questionIndex : nextIndex,
        status: isCompleted ? "completed" : prev.status,
      };
    });
  };

  const handleRestartInterview = () => {
    setSession(initialSession);
  };

  const renderContent = () => {
    switch (session.status) {
      case "onboarding":
        return (
          <InterviewOnboarding
            onStart={handleStartInterview}
            remainingInterviews={remainingInterviews}
            isFreeUser={isFreeUser}
            bonusCredits={bonusCredits}
          />
        );

      case "setup":
        return <InterviewSetup onSetupComplete={handleSetupComplete} />;

      case "interviewing":
        return (
          <InterviewQuestions
            jobTitle={session.jobTitle}
            roleDescription={session.roleDescription}
            questionCount={session.questionCount}
            questions={session.questions}
            currentQuestionIndex={session.currentQuestionIndex}
            onQuestionsGenerated={handleQuestionsGenerated}
            onAnswerSubmitted={handleAnswerSubmitted}
            hellMode={session.hellMode}
          />
        );

      case "completed":
        return (
          <InterviewResults
            session={session}
            onRestart={handleRestartInterview}
            onGoHome={() => navigate("/app")}
          />
        );

      default:
        return null;
    }
  };

  const isHellMode = session.hellMode && (session.status === 'interviewing' || session.status === 'completed');

  // Show locked state for users without access
  if (!hasInterviewAccess) {
    const canPlayTetris = isFreeUser && canEarnBonusCredits();

    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white">
        {/* User bar */}
        <div className="bg-white shadow-sm">
          <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3 sm:gap-4">
              <Logo size="md" onClick={() => navigate("/app")} />
            </div>
            <div className="flex items-center gap-3 sm:gap-4">
              <UserProfileIcon />
            </div>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-4 py-8">
          <div className="text-center">
            {/* Icon */}
            <div className="inline-flex items-center justify-center w-20 h-20 bg-purple-100 rounded-full mb-6">
              <span className="text-4xl">{isFreeUser ? '⏰' : '🎯'}</span>
            </div>

            {/* Title */}
            <h1 className="text-3xl font-bold text-gray-800 mb-4">
              {isFreeUser ? 'Daily Limit Reached' : 'Interview Practice'}
            </h1>

            {/* Description */}
            <p className="text-gray-600 max-w-md mx-auto mb-8">
              {isFreeUser
                ? "You've used your free interview for today. Play Tetris to earn bonus credits or upgrade for unlimited access."
                : 'Upgrade to access AI-powered interview practice with personalized feedback.'}
            </p>

            {/* Tetris button for free users */}
            {canPlayTetris && (
              <button
                onClick={() => setShowTetris(true)}
                className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-colors mb-4"
              >
                <span className="text-xl">🎮</span>
                <span>Play Tetris for +{TETRIS_CREDIT_REWARD} credits</span>
              </button>
            )}

            {/* Upgrade button */}
            <button
              onClick={() => navigate('/pricing')}
              className="block mx-auto px-8 py-3 bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold rounded-lg transition-colors"
            >
              View Plans
            </button>
          </div>
        </div>

        {/* Tetris Game Modal */}
        <TetrisGame
          isOpen={showTetris}
          onClose={() => setShowTetris(false)}
          onComplete={handleTetrisComplete}
        />
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-colors duration-500 ${
      isHellMode
        ? 'bg-gradient-to-b from-gray-950 via-red-950 to-gray-950'
        : 'bg-gradient-to-b from-purple-50 to-white'
    }`}>
      {/* Hell Mode ambient effects */}
      {isHellMode && (
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-red-900/20 via-transparent to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-orange-600/10 to-transparent" />
        </div>
      )}

      {/* User bar */}
      <div className={`shadow-sm relative z-10 ${isHellMode ? 'bg-gray-900/90 border-b border-red-900' : 'bg-white'}`}>
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3 sm:gap-4">
            <Logo size="md" onClick={() => navigate("/app")} />
            <span className={`text-sm hidden sm:block ${isHellMode ? 'text-red-400' : 'text-gray-600'}`}>
              {isHellMode ? '🔥 ' : 'Welcome, '}
              <span className={`font-medium ${isHellMode ? 'text-red-300' : ''}`}>
                {isHellMode ? 'Brave Soul' : currentUser?.email}
              </span>
            </span>
          </div>
          <div className="flex items-center gap-3 sm:gap-4">
            <button
              onClick={handleLogout}
              className={`text-sm flex items-center gap-1 p-2 -m-2 ${
                isHellMode ? 'text-red-400 hover:text-red-300' : 'text-gray-500 hover:text-gray-700'
              }`}
              title="Logout"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8 sm:h-4 sm:w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
              <span className="hidden sm:inline">{isHellMode ? 'Escape' : 'Logout'}</span>
            </button>
            <UserProfileIcon />
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8 relative z-10">
        {renderContent()}
      </div>
    </div>
  );
};

export default InterviewPage;
